import { AppointmentListData } from "@/models/club";
import { faDigits, faNumber } from "./format";
import { CalendarVisit, calendarVisits } from "./upcoming";

export type DayVisit = {
  appointmentId: number;
  key: string;
  start: Date;
  end: Date;
  customer: string;
  service: string;
  phone?: string | null;
  invoiced: boolean;
};

// Free time before the visit whose key it carries.
export type DayGap = { key: string; start: Date; end: Date; minutes: number };

export type StylistDay = {
  total: number;
  done: DayVisit[];
  live: DayVisit | null;
  next: DayVisit | null;
  // Visits that have not started yet, `next` included.
  upcoming: DayVisit[];
  // Free time between the visits still ahead (the one in progress included); the wait for the next visit is not.
  gaps: DayGap[];
  // Finished visits without an invoice, latest first.
  uninvoiced: DayVisit[];
};

// Shorter breaks are setup time, not room for another client.
const MIN_GAP_MINUTES = 15;

function toVisit({ appointment, start, end }: CalendarVisit): DayVisit {
  const customer = `${appointment.customerFirstName ?? ""} ${appointment.customerLastName ?? ""}`.trim();
  return {
    appointmentId: appointment.id ?? 0,
    key: String(appointment.id ?? start.getTime()),
    start,
    end,
    customer: customer || "مشتری بدون نام",
    service: appointment.merchantServiceTitle || "خدمت ثبت‌شده",
    phone: appointment.customerMobileNumber,
    invoiced: Boolean(appointment.purchaseRegistered),
  };
}

// A stylist's day from today's calendar: what is done, what is happening now and what is next.
export function stylistDay(data: AppointmentListData | undefined, merchantUserId?: number, now = new Date()): StylistDay {
  const visits = calendarVisits(data, now)
    .visits.filter(({ appointment, user }) => merchantUserId === undefined || (appointment.merchantUserId ?? user?.id) === merchantUserId)
    .map(toVisit)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const done = visits.filter((visit) => visit.end <= now);
  const live = visits.find((visit) => visit.start <= now && visit.end > now) ?? null;
  const upcoming = visits.filter((visit) => visit.start > now);

  const gaps: DayGap[] = [];
  const ahead = live ? [live, ...upcoming] : upcoming;
  // Overlapping visits push the free time back to the latest end so far.
  let busyUntil = ahead[0]?.end.getTime() ?? 0;
  for (const visit of ahead.slice(1)) {
    const minutes = Math.round((visit.start.getTime() - busyUntil) / 60_000);
    if (minutes >= MIN_GAP_MINUTES) gaps.push({ key: visit.key, start: new Date(busyUntil), end: visit.start, minutes });
    busyUntil = Math.max(busyUntil, visit.end.getTime());
  }

  return {
    total: visits.length,
    done,
    live,
    next: upcoming[0] ?? null,
    upcoming,
    gaps,
    uninvoiced: done.filter((visit) => !visit.invoiced && visit.appointmentId).reverse(),
  };
}

export const clockOf = (date: Date) =>
  faDigits(`${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`);

// "۴۰ دقیقه" / "۲ ساعت" / "۱ ساعت و ۱۵ دقیقه"
export function spanText(minutes: number) {
  const value = Math.max(0, Math.round(minutes));
  if (value < 60) return `${faNumber(value)} دقیقه`;
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return rest ? `${faNumber(hours)} ساعت و ${faNumber(rest)} دقیقه` : `${faNumber(hours)} ساعت`;
}

// "HH:mm" for booking at or after `date`, on the booking form's 5-minute steps (Latin digits, as the form expects).
export function bookingTime(date: Date) {
  const minutes = Math.ceil((date.getHours() * 60 + date.getMinutes()) / 5) * 5;
  return `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

export const minutesUntil = (date: Date, now = new Date()) => Math.max(0, Math.ceil((date.getTime() - now.getTime()) / 60_000));

// For a manager's «سالن | برنامه‌ی من» switch: "الان" while their own visit is in progress, else the
// next visit's time when it starts within the hour.
export function myVisitHint(day: StylistDay, now = new Date()) {
  if (day.live) return "الان";
  if (day.next && minutesUntil(day.next.start, now) <= 60) return clockOf(day.next.start);
  return undefined;
}
